import type { ActivityLeg, ActivityRow } from './activity'
import type { StrategyView } from './api-types'
import { SOL_MINT, tokenLogo } from './default-tokens'
import { strategyTitle } from '@/components/strategy-card'

/*
 * Reports mirror Sonar's exports (Settings > Reports): the same five data
 * types, the same six columns and the same activity labels, so a Dispence
 * file drops into the same spreadsheet as a Sonar one. Dates are written in
 * the viewer's own time zone and the file says which (John, Oct 31 2026).
 */

export type DataType = 'all' | 'strategies' | 'deposits' | 'swaps' | 'withdrawals'

export const DATA_TYPES: { value: DataType; label: string; file: string }[] = [
  { value: 'all', label: 'All activity', file: 'all-activity' },
  { value: 'strategies', label: 'Strategies', file: 'strategies' },
  { value: 'deposits', label: 'Deposits', file: 'deposits' },
  { value: 'swaps', label: 'Swaps', file: 'swaps' },
  { value: 'withdrawals', label: 'Withdraws', file: 'withdrawals' },
]

export type RangeKey = 'all' | 'today' | 'yesterday' | '24h' | '7d' | '30d' | '90d' | 'custom'

export const RANGES: { value: RangeKey; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
]

/** both ends as the date input writes them: YYYY-MM-DD, local days */
export type CustomRange = { from: string; to: string }

export type RangeBounds = { from: Date | null; to: Date | null; label: string }

export type ReportLeg = { amount: number | null; symbol: string; address: string }

/** one line of a report: Sonar's six columns with the numbers still numbers */
export type ReportRow = {
  id: string
  date: Date
  group: Exclude<DataType, 'all'>
  /** Deposit, Deposit Token, Withdraw, Withdraw Token, Swap Buy, Swap Sell, Buy, Sell, Strategy */
  activity: string
  in: ReportLeg | null
  out: ReportLeg | null
  usd: number | null
  /** the full on-chain signature, or the strategy's name on a Strategy line */
  tx: string | null
  txUrl: string | null
  /** every token the line touches, for the token filter */
  tokenKeys: string[]
}

const CHAIN = 'Solana'
const DONE = new Set(['completed', 'confirmed'])
const isSol = (leg: ActivityLeg) => leg.address === SOL_MINT || leg.symbol === 'SOL'

/** finished activity plus one line per strategy, newest first */
export function toReportRows(rows: ActivityRow[], strategies: StrategyView[]): ReportRow[] {
  const out: ReportRow[] = []
  const keysOf = (...legs: (ActivityLeg | ReportLeg | undefined | null)[]) => legs.flatMap((l) => (l ? [l.address] : []))
  for (const r of rows) {
    if (!DONE.has(r.status) || !r.createdAt) continue
    const base = { id: r.id, date: new Date(r.createdAt), usd: r.valueUsd ?? null, tx: r.txId ?? null, txUrl: r.explorerUrl ?? null }
    if (r.kind === 'deposit') {
      const leg = r.receivedLeg ?? null
      out.push({ ...base, group: 'deposits', activity: leg && !isSol(leg) ? 'Deposit Token' : 'Deposit', in: leg, out: null, tokenKeys: keysOf(leg) })
    } else if (r.kind === 'send') {
      const leg = r.paidLeg ?? null
      out.push({ ...base, group: 'withdrawals', activity: leg && !isSol(leg) ? 'Withdraw Token' : 'Withdraw', in: null, out: leg, tokenKeys: keysOf(leg) })
    } else {
      const strategy = r.strategyId !== undefined
      out.push({
        ...base,
        group: strategy ? 'strategies' : 'swaps',
        activity: strategy ? r.type : `Swap ${r.type}`,
        in: r.paidLeg ?? null,
        out: r.receivedLeg ?? null,
        tokenKeys: keysOf(r.paidLeg, r.receivedLeg),
      })
    }
  }
  // the strategy itself is a line of its own, like Sonar's "Strategy" rows: no amounts, so totals stay the fills' totals
  for (const s of strategies) {
    if (!s.createdAt) continue
    const token: ReportLeg = { amount: null, symbol: (s.token.symbol ?? 'TOKEN').toUpperCase(), address: s.token.address }
    const settlement: ReportLeg = { amount: null, symbol: s.settlement.symbol.toUpperCase(), address: s.settlement.address }
    const buy = s.side === 'buy'
    out.push({
      id: `strategy-${s.id}`,
      date: new Date(s.createdAt),
      group: 'strategies',
      activity: 'Strategy',
      in: buy ? settlement : token,
      out: buy ? token : settlement,
      usd: null,
      tx: s.name ?? strategyTitle(s),
      txUrl: null,
      tokenKeys: keysOf(token, settlement),
    })
  }
  out.sort((a, b) => b.date.getTime() - a.date.getTime())
  return out
}

export type TokenOption = { key: string; symbol: string; imageUrl?: string }

/** every token the history touches, SOL first then by ticker */
export function tokenOptions(rows: ActivityRow[], strategies: StrategyView[]): TokenOption[] {
  const map = new Map<string, TokenOption>()
  const add = (key: string, symbol: string, imageUrl: string | undefined) => {
    const existing = map.get(key)
    if (!existing) map.set(key, { key, symbol, imageUrl: imageUrl ?? tokenLogo(key) })
    else if (!existing.imageUrl && imageUrl) existing.imageUrl = imageUrl
  }
  for (const r of rows) {
    for (const leg of [r.paidLeg, r.receivedLeg]) {
      if (leg) add(leg.address, leg.symbol, leg.symbol === r.symbol ? r.imageUrl : undefined)
    }
  }
  for (const s of strategies) {
    add(s.token.address, (s.token.symbol ?? 'TOKEN').toUpperCase(), s.token.imageUrl ?? undefined)
    add(s.settlement.address, s.settlement.symbol.toUpperCase(), undefined)
  }
  return [...map.values()].sort((a, b) => (a.key === SOL_MINT ? -1 : b.key === SOL_MINT ? 1 : a.symbol.localeCompare(b.symbol)))
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const daysBefore = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - n)
const dayLabel = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

function parseDay(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!m) return null
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

/** the range as instants, in the viewer's own time zone; `to` is exclusive */
export function rangeBounds(key: RangeKey, custom: CustomRange, now = new Date()): RangeBounds {
  const label = RANGES.find((r) => r.value === key)?.label ?? 'All time'
  switch (key) {
    case 'today':
      return { from: startOfDay(now), to: null, label }
    case 'yesterday':
      return { from: daysBefore(now, 1), to: startOfDay(now), label }
    case '24h':
      return { from: new Date(now.getTime() - 86_400_000), to: null, label }
    case '7d':
      return { from: new Date(now.getTime() - 7 * 86_400_000), to: null, label }
    case '30d':
      return { from: new Date(now.getTime() - 30 * 86_400_000), to: null, label }
    case '90d':
      return { from: new Date(now.getTime() - 90 * 86_400_000), to: null, label }
    case 'custom': {
      let from = parseDay(custom.from)
      let toDay = parseDay(custom.to)
      if (from && toDay && from > toDay) [from, toDay] = [toDay, from]
      const to = toDay ? daysBefore(toDay, -1) : null
      const text = from && toDay ? `${dayLabel.format(from)} – ${dayLabel.format(toDay)}` : from ? `From ${dayLabel.format(from)}` : toDay ? `Until ${dayLabel.format(toDay)}` : label
      return { from, to, label: text }
    }
    default:
      return { from: null, to: null, label }
  }
}

export type ReportFilter = { dataType: DataType; token: string | null; from: Date | null; to: Date | null }

export function filterReportRows(rows: ReportRow[], f: ReportFilter): ReportRow[] {
  return rows.filter(
    (r) =>
      (f.dataType === 'all' || r.group === f.dataType) &&
      (f.token === null || r.tokenKeys.includes(f.token)) &&
      (f.from === null || r.date >= f.from) &&
      (f.to === null || r.date < f.to),
  )
}

export type TokenTotal = { symbol: string; chain: string; in: number; out: number; hasIn: boolean; hasOut: boolean }

/** per token, what went in and out over the lines, plus the dollar sum (Sonar's footer) */
export function reportTotals(rows: ReportRow[]): { tokens: TokenTotal[]; usd: number; records: number } {
  const map = new Map<string, TokenTotal>()
  const add = (leg: ReportLeg | null, side: 'in' | 'out') => {
    if (!leg || leg.amount === null) return
    const key = `${leg.symbol}|${CHAIN}`
    const total = map.get(key) ?? { symbol: leg.symbol, chain: CHAIN, in: 0, out: 0, hasIn: false, hasOut: false }
    total[side] += leg.amount
    if (side === 'in') total.hasIn = true
    else total.hasOut = true
    map.set(key, total)
  }
  let usd = 0
  for (const r of rows) {
    add(r.in, 'in')
    add(r.out, 'out')
    usd += r.usd ?? 0
  }
  return { tokens: [...map.values()], usd, records: rows.length }
}

/** a line's amount: four places like Sonar, more only when four would read as zero */
export function formatReportAmount(value: number, thousands = false): string {
  if (!Number.isFinite(value)) return '-'
  const digits = value !== 0 && Math.abs(value) < 0.00005 ? 9 : 4
  const text = thousands ? value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : value.toFixed(digits)
  return digits > 4 ? text.replace(/0+$/, '') : text
}

/** a total: up to four places, no trailing zeros (Sonar prints 3520 and 2881.4853) */
export function formatTotalAmount(value: number, thousands = false): string {
  if (!Number.isFinite(value)) return '-'
  if (thousands) return value.toLocaleString('en-US', { maximumFractionDigits: 4 })
  return value.toFixed(4).replace(/\.?0+$/, '')
}

export function formatReportUsd(value: number | null, thousands = false): string {
  if (value === null || !Number.isFinite(value)) return '-'
  return thousands ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `$${value.toFixed(2)}`
}

/** "15512.0043 HELL", "- HELL" on a Strategy line, "-" when the side is empty */
export function legText(leg: ReportLeg | null, thousands = false): string {
  if (!leg) return '-'
  return `${leg.amount === null ? '-' : formatReportAmount(leg.amount, thousands)} ${leg.symbol}`
}

export type ZoneInfo = { timeZone: string; label: string; short: string }

/** the viewer's time zone, named the way people say it: "Central Time", not CDT or CST */
export function zoneInfo(now = new Date()): ZoneInfo {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  const name = (style: 'long' | 'short') =>
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: style }).formatToParts(now).find((p) => p.type === 'timeZoneName')?.value
  const long = name('long') ?? timeZone
  const label = /\bTime$/.test(long) ? long.replace(/\b(Daylight|Standard|Summer)\s+/, '') : long
  return { timeZone, label, short: name('short') ?? '' }
}

const dateFormats = new Map<string, Intl.DateTimeFormat>()

/** "Oct 24, 2026, 11:38 AM" in the given zone (Sonar's date column, converted) */
export function formatReportDate(date: Date, timeZone: string): string {
  let format = dateFormats.get(timeZone)
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    dateFormats.set(timeZone, format)
  }
  // newer engines put a narrow no-break space before AM/PM; files want a plain one
  return format.format(date).replace(/[  ]/g, ' ')
}

const csvField = (value: string) => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value)

/** Sonar's CSV, column for column, with the dates in the viewer's zone and the full signatures */
export function buildCsv(rows: ReportRow[], zone: ZoneInfo): string {
  const lines = [`Date (${zone.label}),Activity,In,Out,USD Value,Transaction`]
  for (const r of rows) {
    lines.push([`"${formatReportDate(r.date, zone.timeZone)}"`, csvField(r.activity), csvField(legText(r.in)), csvField(legText(r.out)), formatReportUsd(r.usd), csvField(r.tx ?? '-')].join(','))
  }
  const totals = reportTotals(rows)
  for (const t of totals.tokens) {
    lines.push(`,TOTAL - ${csvField(`${t.symbol} (${t.chain})`)},${t.hasIn ? formatTotalAmount(t.in) : '-'},${t.hasOut ? formatTotalAmount(t.out) : '-'},,`)
  }
  lines.push(`,TOTAL USD,,,${formatReportUsd(totals.usd)},`)
  return `﻿${lines.join('\r\n')}\r\n`
}

/** dispence-swaps-2026-10-31 (the viewer's own date) */
export function reportFileName(dataType: DataType, now = new Date()): string {
  const file = DATA_TYPES.find((d) => d.value === dataType)?.file ?? 'activity'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `dispence-${file}-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** hands the browser a file to save */
export function saveFile(data: string | Uint8Array, name: string, type: string) {
  const part: BlobPart = typeof data === 'string' ? data : new Uint8Array(data)
  const url = URL.createObjectURL(new Blob([part], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
